import React, { useRef } from 'react'; import { Link, useNavigate } from 'react-router-dom';
import {
  Package, ShoppingCart, BarChart2, Users, Nut, Shield,
  CheckCircle, ArrowRight, ChevronDown, Star, Clock,
  Eye, EyeOff, Loader, Sun, Moon,
} from 'lucide-react';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useMeta } from '../../hooks/useMeta';
import { useNombreApp } from '../../hooks/useNombreApp';

import { MOSTRAR_PLANES } from '../../config/comercial';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';


// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar({ onRegisterClick }) {
  const navigate = useNavigate();
  const nombreApp = useNombreApp();
  const { isDark, toggleTheme } = useTheme();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-negro-950/95 backdrop-blur-xl border-b border-negro-800">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
            <Nut size={16} className="text-negro-950" />
          </div>
          <span className="font-bold text-lg tracking-tight text-white">{nombreApp}</span>
        </div>

        {/* Links */}
        <div className="hidden md:flex items-center gap-6 text-sm font-medium">
          <a href="#funcionalidades" className="text-negro-300 hover:text-white transition-colors">Funcionalidades</a>
          {MOSTRAR_PLANES && (
            <a href="#planes"        className="text-negro-300 hover:text-white transition-colors">Planes</a>
          )}
          <a href="#registro"        className="text-negro-300 hover:text-white transition-colors">Registro</a>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            title={isDark ? 'Modo claro' : 'Modo oscuro'}
            className="p-2 rounded-xl border border-negro-700 text-negro-300 hover:bg-negro-800 hover:text-white transition-all"
          >
            {isDark ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            onClick={() => navigate('/login')}
            className="text-sm font-semibold px-3 py-1.5 rounded-lg text-negro-300 hover:text-white transition-colors"
          >
            Iniciar sesión
          </button>
          <button
            onClick={onRegisterClick}
            className="bg-brand-600 hover:bg-brand-500 text-negro-950 text-sm font-bold px-4 py-2 rounded-xl transition-all shadow-md shadow-brand-500/20"
          >
            Probá gratis
          </button>
        </div>
      </div>
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero({ onRegisterClick }) {
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-950">
      {/* Decoración de fondo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-brand-500/10 dark:bg-brand-600/20 rounded-full blur-[120px]" />
        <div className="absolute top-10 right-10 w-64 h-64 bg-brand-400/10 dark:bg-brand-600/8 rounded-full blur-[80px]" />
      </div>
      {/* Grid sutil */}
      <div
        className="absolute inset-0 opacity-[0.035] dark:opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(#94a3b8 1px,transparent 1px),linear-gradient(90deg,#94a3b8 1px,transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto text-center px-6 py-24">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-brand-400 border border-brand-500 text-negro-900 dark:bg-brand-500 dark:border-brand-400 dark:text-negro-950 rounded-full px-4 py-1.5 text-sm font-semibold mb-8">
          <Nut size={14} />
          Sistema de gestión para ferreterías
        </div>

        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-tight mb-6 text-slate-900 dark:text-white">
          Tu ferretería,{' '}
          <span className="bg-brand-400 text-negro-950 px-3 -mx-1 inline-block rounded">
            organizada
          </span>{' '}
          desde el día uno
        </h1>

        <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          Controlá el mostrador, el depósito y las cuentas de tu ferretería desde un solo lugar.
          Sin complicaciones, sin costos de instalación.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={onRegisterClick}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-600 hover:bg-brand-500 text-negro-950 font-bold rounded-2xl text-lg transition-all shadow-xl shadow-brand-500/25 hover:-translate-y-0.5"
          >
            Probá gratis <ArrowRight size={20} />
          </button>
          <a
            href="#funcionalidades"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-2xl text-lg border border-slate-200 dark:border-slate-700 shadow-sm transition-all"
          >
            Ver funcionalidades <ChevronDown size={20} />
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-slate-500 dark:text-slate-500">
          <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-800" /> Sin tarjeta de crédito</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-800" /> Configuración en minutos</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-800" /> Soporte incluido</span>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-slate-400 dark:text-slate-600">
        <ChevronDown size={24} />
      </div>
    </section>
  );
}

// ── Funcionalidades ───────────────────────────────────────────────────────────
const FEATURES = [
  { icon: Package,      title: 'Inventario inteligente',  desc: 'Controlá stock en tiempo real. Alertas automáticas cuando un producto está por agotarse.' },
  { icon: ShoppingCart, title: 'Punto de venta rápido',   desc: 'Emitís tickets en segundos. Buscá por código de barras, nombre o categoría.' },
  { icon: BarChart2,    title: 'Reportes en tiempo real', desc: 'Dashboard con ventas del día, semana y mes. Análisis de rentabilidad por producto.' },
  { icon: Users,        title: 'Gestión de clientes',     desc: 'Registrá tus clientes, historial de compras y manejá cuentas corrientes fácilmente.' },
  { icon: Shield,       title: 'Usuarios y roles',        desc: 'Cajeros, supervisores y administradores con permisos diferenciados por cada rol.' },
  { icon: Star,         title: 'Ticket 80mm',             desc: 'Comprobantes de venta profesionales compatibles con cualquier impresora térmica estándar.' },
];

function Features() {
  return (
    <section id="funcionalidades" className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-black mb-4 text-slate-900 dark:text-white">Todo lo que necesitás</h2>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            Funcionalidades pensadas para ferreterías paraguayas. Simples, rápidas y que funcionan.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 hover:shadow-md dark:hover:border-slate-600 transition-all group">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-500 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                <f.icon size={22} className="text-negro-950" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-slate-900 dark:text-white">{f.title}</h3>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Planes ────────────────────────────────────────────────────────────────────
function Plans({ onRegisterClick, planes, ciclo, setCiclo }) {
  if (!planes || planes.length === 0) return null;

  const hayAnual = planes.some(p => parseFloat(p.descuento_anual) > 0);

  // En ciclo anual solo se muestran los planes que tienen descuento configurado
  const planesVisibles = ciclo === 'anual'
    ? planes.filter(p => parseFloat(p.descuento_anual) > 0)
    : planes;

  const precioMostrado = (plan) => {
    const base = parseFloat(plan.price);
    if (ciclo === 'anual') {
      const desc = parseFloat(plan.descuento_anual || 0);
      return Math.round(base * 12 * (1 - desc / 100));
    }
    return base;
  };

  const ahorro = (plan) => {
    const sinDesc = parseFloat(plan.price) * 12;
    const conDesc = Math.round(sinDesc * (1 - parseFloat(plan.descuento_anual) / 100));
    return Math.round(sinDesc - conDesc);
  };

  return (
    <section id="planes" className="py-24 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-black mb-4 text-slate-900 dark:text-white">Planes y precios</h2>
          <p className="text-lg text-slate-500 dark:text-slate-400">Empezá gratis. Escalá cuando lo necesités.</p>

          {/* Toggle mensual / anual — solo si hay al menos un plan con descuento anual */}
          {hayAnual && (
            <div className="inline-flex items-center gap-1 mt-6 bg-slate-200 dark:bg-slate-800 p-1 rounded-xl">
              {['mensual', 'anual'].map(c => (
                <button
                  key={c}
                  onClick={() => setCiclo(c)}
                  className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                    ciclo === c
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  {c === 'mensual' ? 'Mensual' : 'Anual'}
                  {c === 'anual' && (
                    <span className="ml-2 text-[10px] font-black text-exito-600 dark:text-exito-400 bg-exito-100 dark:bg-exito-500/20 px-1.5 py-0.5 rounded-full">
                      AHORRÁS
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`grid grid-cols-1 ${planesVisibles.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-6`}>
          {planesVisibles.map((plan, i) => {
            const esPopular = i === Math.floor(planesVisibles.length / 2);
            const precio = precioMostrado(plan);
            const ahorroAnual = ciclo === 'anual' ? ahorro(plan) : null;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-8 border transition-all ${
                  esPopular
                    ? 'bg-brand-600 border-brand-600 shadow-2xl shadow-brand-500/30'
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm'
                }`}
              >
                {esPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-white text-brand-800 text-xs font-black px-3 py-1 rounded-full shadow">MÁS POPULAR</span>
                  </div>
                )}

                <h3 className={`font-bold text-xl mb-2 ${esPopular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>{plan.name}</h3>

                <div className="mb-1">
                  <span className={`text-3xl font-black ${esPopular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                    {plan.currency === 'PYG' ? `Gs. ${precio.toLocaleString('es-PY')}` : `USD ${precio}`}
                  </span>
                  <span className={`text-sm ml-1 ${esPopular ? 'text-brand-100' : 'text-slate-400 dark:text-slate-500'}`}>
                    {ciclo === 'anual' ? '/año' : '/mes'}
                  </span>
                </div>

                {ciclo === 'anual' && ahorroAnual > 0 && (
                  <p className={`text-xs font-bold mb-3 ${esPopular ? 'text-exito-200' : 'text-exito-600 dark:text-exito-400'}`}>
                    Ahorrás Gs. {ahorroAnual.toLocaleString('es-PY')} vs pago mensual ({plan.descuento_anual}% off)
                  </p>
                )}
                {ciclo === 'mensual' && <div className="mb-4" />}

                {plan.description && (
                  <p className={`text-sm mb-6 ${esPopular ? 'text-brand-100' : 'text-slate-500 dark:text-slate-400'}`}>{plan.description}</p>
                )}

                <ul className="space-y-2.5 mb-8">
                  {[
                    `Hasta ${parseInt(plan.max_products).toLocaleString('es-PY')} productos`,
                    `Hasta ${plan.max_users} usuarios`,
                    'Dashboard y reportes',
                    'Impresión de tickets',
                  ].map((item, j) => (
                    <li key={j} className={`flex items-center gap-2 text-sm ${esPopular ? 'text-brand-50' : 'text-slate-600 dark:text-slate-300'}`}>
                      <CheckCircle size={14} className={esPopular ? 'text-brand-200' : 'text-brand-800'} />
                      {item}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onRegisterClick}
                  className={`w-full py-3 rounded-xl font-bold transition-all ${
                    esPopular
                      ? 'bg-white hover:bg-brand-50 text-brand-800'
                      : 'bg-brand-600 hover:bg-brand-500 text-negro-950 shadow-md shadow-brand-500/20'
                  }`}
                >
                  Empezar ahora
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm mt-8 text-slate-400 dark:text-slate-500">
          ¿Necesitás algo más?{' '}
          <span className="text-slate-500 dark:text-slate-400">Contactanos para un plan personalizado.</span>
        </p>
      </div>
    </section>
  );
}

// ── Formulario de registro ────────────────────────────────────────────────────
function RegisterForm({ formRef }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ nombre_negocio: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/api/v1/auth/registro/`, form);
      setSuccess(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Ocurrió un error. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={formRef} id="registro" className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-md mx-auto px-6">
        {success ? (
          <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-3xl p-10 shadow-xl text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-exito-50 dark:bg-exito-500/10 border border-exito-200 dark:border-exito-500/20 rounded-2xl mb-6">
              <CheckCircle size={36} className="text-exito-500" />
            </div>
            <h2 className="text-3xl font-black mb-3 text-slate-900 dark:text-white">¡Listo!</h2>
            <p className="text-lg mb-2 text-slate-600 dark:text-slate-300">{success.message}</p>
            {success.trial_days > 0 && (
              <p className="text-sm mb-8 flex items-center justify-center gap-1.5 text-slate-500 dark:text-slate-400">
                <Clock size={14} className="text-brand-800" />
                {success.trial_days} días con el plan{' '}
                <strong className="text-brand-800 dark:text-brand-400">{success.plan_name}</strong>
              </p>
            )}
            <button
              onClick={() => navigate('/login')}
              className="w-full py-4 bg-brand-600 hover:bg-brand-500 text-negro-950 font-bold rounded-2xl text-lg transition-all shadow-lg shadow-brand-500/25 flex items-center justify-center gap-2"
            >
              Ingresar a mi negocio <ArrowRight size={20} />
            </button>
            <p className="text-xs mt-4 text-slate-400 dark:text-slate-500">
              Usá el correo y contraseña que registraste para ingresar.
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <h2 className="text-4xl font-black mb-3 text-slate-900 dark:text-white">Empezá gratis</h2>
              <p className="text-slate-500 dark:text-slate-400">Creá tu cuenta en menos de un minuto. Sin tarjeta de crédito.</p>
            </div>

            <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="registro-negocio" className="block text-xs font-semibold mb-1.5 uppercase tracking-wider text-slate-600 dark:text-slate-400">
                    Nombre de tu ferretería
                  </label>
                  <input id="registro-negocio"
                    type="text"
                    name="nombre_negocio"
                    required
                    maxLength={80}
                    placeholder="Ej: Ferretería San Roque"
                    value={form.nombre_negocio}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-xl text-sm transition-colors bg-white dark:bg-slate-800/60 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                  />
                </div>

                <div>
                  <label htmlFor="registro-correo" className="block text-xs font-semibold mb-1.5 uppercase tracking-wider text-slate-600 dark:text-slate-400">
                    Tu correo electrónico
                  </label>
                  <input id="registro-correo"
                    type="email"
                    name="email"
                    required
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-xl text-sm transition-colors bg-white dark:bg-slate-800/60 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                  />
                </div>

                <div>
                  <label htmlFor="registro-clave" className="block text-xs font-semibold mb-1.5 uppercase tracking-wider text-slate-600 dark:text-slate-400">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input id="registro-clave"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      required
                      minLength={8}
                      placeholder="Mínimo 8 caracteres"
                      value={form.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 pr-10 border rounded-xl text-sm transition-colors bg-white dark:bg-slate-800/60 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-negro-950 font-bold rounded-2xl text-lg transition-all shadow-lg shadow-brand-500/25 hover:-translate-y-0.5 flex items-center justify-center gap-2 mt-2"
                >
                  {loading
                    ? <><Loader size={18} className="animate-spin" /> Creando tu cuenta...</>
                    : <> Crear mi cuenta gratis <ArrowRight size={18} /></>
                  }
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-slate-100 dark:border-slate-800 text-center">
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  ¿Ya tenés cuenta?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-brand-800 dark:text-brand-400 hover:text-brand-800 font-semibold transition-colors"
                  >
                    Iniciá sesión
                  </button>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const nombreApp = useNombreApp();
  return (
    <footer className="bg-negro-950 border-t border-negro-800 py-10">
      <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-negro-300">
          <Nut size={16} className="text-brand-800 dark:text-brand-400" />
          <span className="font-semibold text-white">{nombreApp}</span>
          <span>— Sistema de gestión para ferreterías</span>
        </div>
        <div className="flex flex-col md:flex-row items-center gap-4">
          {/* En el pie y no escondidos: alguien tiene que poder leerlos antes
              de crear una cuenta. */}
          <div className="flex items-center gap-4 text-sm">
            <Link to="/terminos" className="text-negro-300 hover:text-white">Términos de uso</Link>
            <Link to="/privacidad" className="text-negro-300 hover:text-white">Privacidad</Link>
          </div>
          <p className="text-sm text-negro-400">© {new Date().getFullYear()} SoftecPY. Paraguay.</p>
        </div>
      </div>
    </footer>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function LandingPage() {
  const formRef = useRef(null);
  const nombreApp = useNombreApp();
  const [planes, setPlanes] = useState([]);
  const [ciclo, setCiclo] = useState('mensual');

  useEffect(() => {
    axios.get(`${BASE_URL}/api/v1/public/planes-publicos/`)
      .then(r => setPlanes(r.data.planes || r.data))
      .catch(() => {});
  }, []);

  useMeta({ title: `${nombreApp} — Sistema de gestión para ferreterías`, favicon: '/favicon-landing.svg' });

  const scrollToRegister = () =>
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-200">
      <Navbar onRegisterClick={scrollToRegister} />
      <div className="pt-16">
        <Hero onRegisterClick={scrollToRegister} />
        <Features />
        {MOSTRAR_PLANES && (
          <Plans onRegisterClick={scrollToRegister} planes={planes} ciclo={ciclo} setCiclo={setCiclo} />
        )}
        <RegisterForm formRef={formRef} />
        <Footer />
      </div>
    </div>
  );
}
